import React, { useContext, useEffect, useRef, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import Slide from '@mui/material/Slide';
import Grid from '@mui/material/Grid';
import { Box } from '@mui/material';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import { format } from "date-fns";
import { DataGrid } from "@mui/x-data-grid";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Checkbox from '@mui/material/Checkbox';
import { AppContext } from "../context/AppProvider";
import { AuthContext } from "../context/AuthProvider";
import { cloneDeep, find, forEach, includes, toNumber, filter as filterBy, last } from "lodash";
import moment from 'moment';
import useGroups from '../hooks/useGroups';
import useSubgroups from '../hooks/useSubgroups';
import useCountries from '../hooks/useCountries';
import useSizes from '../hooks/useSizes';
import { fetchWithInterceptor } from '../utils/fetchWithInterceptor';

const PAGE_SIZE = 20;
const ROW_HEIGHT = 52;


const columns = [
  {
    field: "qty",
    headerName: "QTY",
    width: 120,
    sortable: true,
    editable: true,
  },
  {
    field: "sku",
    headerName: "SKU",
    width: 120,
    sortable: true,
  },
  {
    field: "description",
    headerName: "DESCRIPTION",
    width: 400,
    sortable: true,
    renderCell: (params) => {
      return (
        <Tooltip title={params?.row?.description} placement="right">
          <span>{params?.row?.description}</span>
        </Tooltip>
      );
    },
  },
  {
    field: "size",
    headerName: "SIZE",
    width: 100,
    sortable: true,
  },
  {
    field: "units",
    headerName: "UNITS",
    width: 100,
    sortable: true,
  },
  {
    field: "availability",
    headerName: "AVAILABILITY",
    width: 150,
    sortable: true,
    renderCell: (params) => {
      if (params?.row?.availability === "Y") {
        return <CheckIcon color="action" />
      }
      return <CloseIcon color="action" />;
    },
  },
  {
    field: "priceChangeAmount",
    headerName: "PRICE CHANGE AMOUNT",
    width: 120,
    sortable: true,
    valueGetter: (value) =>
      value
        ? Number(value).toFixed(2)
        : "",
  },
  {
    field: "priceChangeDirection",
    headerName: "PRICE CHANGE DIRECTION",
    width: 140,
    sortable: true,
    renderCell: (params) => {
      if (params?.row?.priceChangeDirection === "I") {
        return <ArrowUpwardIcon color="error" />
      }
      return <ArrowDownwardIcon color="success" />;
    },
  },
  {
    field: "priceEffectiveDate",
    headerName: "PRICE EFFECTIVE DATE",
    width: 140,
    sortable: true,
    valueGetter: (value) =>
      value
        ? format(new Date(value), "yyyy-MM-dd")
        : "",
  },
  {
    field: "casePrice",
    headerName: "CASE PRICE",
    width: 120,
    sortable: true,
    valueGetter: (value) =>
      value
        ? Number(value).toFixed(2)
        : "",
  },
  {
    field: "unitPrice",
    headerName: "UNIT PRICE",
    width: 120,
    sortable: true,
    valueGetter: (value) =>
      value
        ? Number(value).toFixed(2)
        : "",
  },
  {
    field: "priceChangePercentage",
    headerName: "PERCENTAGE",
    width: 150,
    sortable: true,
    valueGetter: (value) => `${value}%`,
  },  {
    field: "priceChangeNominal",
    headerName: "NOMINAL",
    width: 120,
    sortable: true,
    valueGetter: (value) =>
      value
        ? Number(value).toFixed(2)
        : "",
  },
  {
    field: "country",
    headerName: "COUNTRY",
    width: 180,
    sortable: true,
  },
  {
    field: "productGroup",
    headerName: "PRODUCT GROUP",
    width: 200,
    sortable: true,
  },
  {
    field: "productSubgroup",
    headerName: "PRODUCT SUBGROUP",
    width: 200,
    sortable: true,
    renderCell: (params) => {
      return (
        <Tooltip title={params?.row?.productSubgroup} placement="right">
          <span>{params?.row?.productSubgroup}</span>
        </Tooltip>
      );
    },
  },
];												

const defaultFilter = {
  sku: "",
  availability: "",
  priceChangeDirection: "",
  dateFrom: null,
  dateTo: null,
  groups: [],
  subgroups: [],
  countries: [],
  sizes: [],
  percentageFrom: "",
  percentageTo: "",
  description: "",
};

const defaultOrder = [
  { field: 'priceChangeDirection', sort: "asc" }, 
  { field: 'availability', sort:  "desc" }, 
  { field: 'priceEffectiveDate', sort:  "asc" }
];

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function LiquorConnectProductsDialog({ open, onClose, id }) {
  const appContext = useContext(AppContext);
  const authContext = useContext(AuthContext);

  const [selectionModel, setSelectionModel] = useState({ type: 'include', ids: new Set() });
  const [sortModel, setSortModel] = useState(defaultOrder);
  const [filter, setFilter] = useState(defaultFilter);
  const [quantities, setQuantities] = useState({});

  const [products, setProducts] = useState([]);

  const [isLoading, setIsLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: PAGE_SIZE,
  });
  const [rowCount, setRowCount] = useState(0);

  const [subgroups, setSubgroups] = useState([]);

  const [filterVisible, setFilterVisible] = useState(true);

  const groups = useGroups(id);
  const allSubgroups = useSubgroups(id);
  const countries = useCountries(id);
  const sizes = useSizes(id);  

  const toggleShowFilter = () => setFilterVisible(!filterVisible);

  const handleReset = () => {
    setFilter(cloneDeep(defaultFilter));
    setSortModel(cloneDeep(defaultOrder));
  }

  useEffect(() => {
    if (filter.groups.length) {
      const subgroupsFiltered = filterBy(allSubgroups, (s) => includes(filter.groups, s?.groupName ));
      setSubgroups(subgroupsFiltered);
    } else {
      setSubgroups(allSubgroups);
    }
    setFilter((f) => ({ ...f, subgroups: [] }));
  }, [filter.groups, allSubgroups]);

  let active = useRef(false);

  const getOrderBy = () => {
    const orderBy = [];
    forEach(sortModel, (model) => {
      orderBy.push({ [model.field]: model.sort });
    });
    return JSON.stringify(orderBy);
  };

  const loadProducts = async (reset) => {
    if (!id) {
      return;
    }
    setIsLoading(true);
    const oldSelection = cloneDeep(selectionModel);
    let filters = {
      id: toNumber(id),
      limit: PAGE_SIZE,
      offset: reset ? 0 : paginationModel.page * PAGE_SIZE,
      orderBy: getOrderBy()
    };
    if (filter.sku) {
      filters.sku = filter.sku;
    }
    if (filter.availability) {
      filters.availability = filter.availability;
    }
    if (filter.priceChangeDirection) {
      filters.priceChangeDirection = filter.priceChangeDirection;
    }
    if (filter.dateFrom) {
      filters.dateFrom = moment(filter.dateFrom).startOf('day').format();
    }
    if (filter.dateTo) {
      filters.dateTo = moment(filter.dateTo).endOf('day').format();
    }
    if (filter.groups.length) {
      filters.groups = filter.groups.join(",");
    }
    if (filter.subgroups.length) {
      filters.subgroups = filter.subgroups.join(",");
    }
    if (filter.countries.length) {
      filters.countries = filter.countries.join(",");
    }
    if (filter.sizes.length) {
      filters.sizes = filter.sizes.join(",");
    }
    if (filter.percentageFrom) {
      filters.percentageFrom = filter.percentageFrom;
    }
    if (filter.percentageTo) {
      filters.percentageTo = filter.percentageTo;
    }
    if (filter.description) {
      filters.description = filter.description;
    }
    const params = new URLSearchParams(filters);
    const response = await fetchWithInterceptor(
      `/api/integrations/liquor-connect-products/?${params}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-access-token": authContext.getToken(),
        },
        
      }
    );
    if (response.ok) {
      if (!active.current) {
        return;
      }

      const data = await response.json();
      setProducts(data.products);
      if (reset) {
        setPaginationModel({ ...paginationModel, page: 0 });
      }
      setRowCount(data.total);
      setSelectionModel(oldSelection);
    } else {
      console.log(response);
      appContext.showMessage(
        "There was an error getting the products",
        "error"
      );
    }
    setIsLoading(false);
  };

  const updateQuantity = async (id, qty) => {
    await fetchWithInterceptor(`/api/integrations/liquor-connect-products/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ qty }),
      headers: {
        "Content-Type": "application/json",
        'x-access-token': authContext.getToken()
      },
    });
  }

  const handleProcessRowUpdate = async (updatedRow, originalRow) => {
    setQuantities(q => ({ ...q, [updatedRow.id]: updatedRow.qty }));
    await updateQuantity(updatedRow.id, updatedRow.qty);
    return { ...updatedRow }
  }

  const handleProcessRowUpdateError = (error) => {
    console.log(error);
  }

  const handleChangeMultipleSelect = (event, name) => {
    if (last(event.target.value) === "") {
      setFilter({ ...filter, [name]: [] })
    } else {
      setFilter({ ...filter, [name]: event.target.value })
    }
  }

  const handleDownload = async () => {
    const selectedIds = selectionModel.ids.values();

    const data = Array.from(selectedIds).map((id) => ({ id, qty: quantities[id] || 0 }));
    
    const params = new URLSearchParams({ data: JSON.stringify(data) });
    const response = await fetchWithInterceptor(`/api/integrations/liquor-connect/download/?${params}`, {
      method: 'GET',
      headers: {
        'x-access-token': authContext.getToken()
      },
    });
    if (response.ok) {
      const blob = await response.blob();
      var a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.setAttribute("download", `${Date.now()}_liquor_connect_file.txt`);
      a.click();
    }
  }

  useEffect(() => {
    active.current = true;

    return () => {
      active.current = false;
    };
  }, []);

  useEffect(() => {
    loadProducts(false);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel.page]);

  useEffect(() => {
    loadProducts(true);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, filter, sortModel]);

  const isValidPercentage = () => {
    try {
      if (!filter.percentageFrom || !filter.percentageTo) {
        return true;
      }
      return toNumber(filter.percentageFrom) <= toNumber(filter.percentageTo);
    } catch (error) {
      return false;
    }
  }

  return (
    <Dialog fullScreen open={open} onClose={onClose} TransitionComponent={Transition}>
      <AppBar sx={{ position: 'relative' }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" sx={{ ml: 2, flex: 1 }}>
            Liquor Connect Products
          </Typography>
        </Toolbar>
      </AppBar>
      <div style={{ padding: 16 }}>
        <Typography variant="h6">Search Filters</Typography>
        <br />
        <Grid container spacing={2} alignItems="center">
          {filterVisible && (
            <>
              <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                <TextField
                  label="SKU"
                  variant="outlined"
                  value={filter.sku}
                  onChange={(e) =>
                    setFilter({ ...filter, sku: e.target.value })
                  }
                  color="primary"
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                <TextField
                  label="Description"
                  variant="outlined"
                  value={filter.description}
                  onChange={(e) =>
                    setFilter({ ...filter, description: e.target.value })
                  }
                  color="primary"
                  fullWidth
                />
              </Grid>

              <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                <FormControl variant="outlined" fullWidth>
                  <InputLabel id="select-availability-label">Availability</InputLabel>
                  <Select
                    labelId="select-availability-label"
                    id="select-availability"
                    value={filter.availability}
                    onChange={(e) => setFilter({ ...filter, availability: e.target.value })}
                    label="Availability"
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    <MenuItem value="Y">Available</MenuItem>
                    <MenuItem value="N">Unavailable</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                <FormControl variant="outlined" fullWidth>
                  <InputLabel id="select-direction-label">Price Change Direction</InputLabel>
                  <Select
                    labelId="select-direction-label"
                    id="select-direction"
                    value={filter.priceChangeDirection}
                    onChange={(e) => setFilter({ ...filter, priceChangeDirection: e.target.value })}
                    label="Price Change Direction"
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    <MenuItem value="I">Increase</MenuItem>
                    <MenuItem value="D">Decrease</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                <DatePicker
                  label="Price Effective From"
                  value={filter.dateFrom}
                  onChange={(date) => setFilter({ ...filter, dateFrom: date })}
                  format="yyyy-MM-dd"
                  slotProps={{ textField: { variant: "outlined", fullWidth: true } }}
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                <DatePicker
                  label="Price Effective To"
                  value={filter.dateTo}
                  onChange={(date) => setFilter({ ...filter, dateTo: date })}
                  format="yyyy-MM-dd"
                  slotProps={{ textField: { variant: "outlined", fullWidth: true } }}
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                <FormControl variant="outlined" fullWidth>
                  <InputLabel id="groups-filter">Groups</InputLabel>
                  <Select
                    labelId="groups-filter"
                    id="groups-filter-checkbox"
                    multiple
                    label="Groups"
                    value={filter.groups}
                    onChange={(event) => handleChangeMultipleSelect(event, "groups")}
                    renderValue={(selected) => selected.map((value) => find(groups, { value }).label).join(", ")}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {groups.map(({ value, label }) => (
                      <MenuItem key={value} value={value}>
                        <Checkbox checked={filter.groups.indexOf(value) > -1} />
                        <ListItemText primary={label} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                <FormControl variant="outlined" fullWidth>
                  <InputLabel id="subgroups-filter">Subgroups</InputLabel>
                  <Select
                    labelId="subgroups-filter"
                    id="subgroups-filter-checkbox"
                    multiple
                    label="Subgroups"
                    value={filter.subgroups}
                    onChange={(event) => handleChangeMultipleSelect(event, "subgroups")}
                    renderValue={(selected) => selected.map((value) => find(subgroups, { value }).label).join(", ")}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {subgroups.map(({ value, label }) => (
                      <MenuItem key={value} value={value}>
                        <Checkbox checked={filter.subgroups.indexOf(value) > -1} />
                        <ListItemText primary={label} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                <FormControl variant="outlined" fullWidth>
                  <InputLabel id="countries-filter">Countries</InputLabel>
                  <Select
                    labelId="countries-filter"
                    id="countries-filter-checkbox"
                    multiple
                    label="Countries"
                    value={filter.countries}
                    onChange={(event) => handleChangeMultipleSelect(event, "countries")}
                    renderValue={(selected) => selected.map((value) => find(countries, { value }).label).join(", ")}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {countries.map(({ value, label }) => (
                      <MenuItem key={value} value={value}>
                        <Checkbox checked={filter.countries.indexOf(value) > -1} />
                        <ListItemText primary={label} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                <FormControl variant="outlined" fullWidth>
                  <InputLabel id="sizes-filter">Sizes</InputLabel>
                  <Select
                    labelId="sizes-filter"
                    id="sizes-filter-checkbox"
                    multiple
                    label="Sizes"
                    value={filter.sizes}
                    onChange={(event) => handleChangeMultipleSelect(event, "sizes")}
                    renderValue={(selected) => selected.map((value) => find(sizes, { value }).label).join(", ")}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {sizes.map(({ value, label }) => (
                      <MenuItem key={value} value={value}>
                        <Checkbox checked={filter.sizes.indexOf(value) > -1} />
                        <ListItemText primary={label} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                <TextField
                  label="Percentage from"
                  variant="outlined"
                  value={filter.percentageFrom}
                  onChange={(e) =>
                    setFilter({ ...filter, percentageFrom: e.target.value })
                  }
                  type="number"
                  color="primary"
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                <TextField
                  label="Percentage to"
                  variant="outlined"
                  value={filter.percentageTo}
                  onChange={(e) =>
                    setFilter({ ...filter, percentageTo: e.target.value })
                  }
                  error={!isValidPercentage()}
                  type="number"
                  color="primary"
                  fullWidth
                />
              </Grid>
            </>
          )}
          <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
            <Grid size={{ xs: 6, sm: 12, md: 12 }}>
              <Button color="primary" onClick={toggleShowFilter} style={{ textTransform: "none" }}>{`${filterVisible ? 'Hide' : 'Show'} filters`}</Button>
            </Grid>
          </Box>
          <Grid size={{ xs: 6, sm: 12, md: 12 }} style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button color="primary" onClick={handleReset}>Reset filters</Button>
          </Grid>
        </Grid>
      </div>

      <Divider />

      <Grid container justifyContent="flex-end" style={{ padding: 16 }}>
        <Grid>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleDownload}
            startIcon={<CheckIcon />}
            disabled={!selectionModel.ids.size}
          >
            Download selection
          </Button>
        </Grid>
      </Grid>

      <DataGrid
        rows={products}
        columns={columns}
        pageSize={PAGE_SIZE}
        pageSizeOptions={[PAGE_SIZE]}
        disableSelectionOnClick
        disableColumnFilter
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        paginationMode="server"
        rowCount={rowCount}
        loading={isLoading}
        rowHeight={ROW_HEIGHT}
        checkboxSelection
        rowSelectionModel={selectionModel}
        onRowSelectionModelChange={(model) => setSelectionModel(model)}
        isRowSelectable={(params) => true}
        processRowUpdate={handleProcessRowUpdate}
        onProcessRowUpdateError={handleProcessRowUpdateError}
        sortingMode="server"
        onSortModelChange={(model) => setSortModel(model)}
        sx={{
          height: ROW_HEIGHT * PAGE_SIZE + 120,
          border: "none",
          "& .MuiDataGrid-menuIcon": {
            display: "none",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            textTransform: "uppercase",
            fontWeight: 600,
            fontSize: "0.75rem",
            color: "rgba(58, 53, 65, 0.87)",
            whiteSpace: "wrap",
            lineHeight: 1,
          },
          "& .MuiDataGrid-cell--withRenderer": {
            display: "flex",
            textOverflow: "unset",
          },
          "& .MuiDataGrid-cell--withRenderer span": {
            overflow: "hidden",
            textWrap: "nowrap",
            textOverflow: "ellipsis",
            display: "block",
          },
          "& .MuiDataGrid-cell--withRenderer .arrow-upward-icon": {
            color: (theme) => theme.palette.success.main,
          },
          "& .MuiDataGrid-columnsContainer": {
            backgroundColor: "rgb(249, 250, 252)",
            justifyContent: "center",
          },
          "& .MuiChip-root.a_error": {
            color: "rgb(97, 26, 21)",
            backgroundColor: "rgb(253, 236, 234)",
          },
          "& .MuiChip-root.b_modified": {
            color: "rgb(30, 70, 32)",
            backgroundColor: "rgb(237, 247, 237)",
          },
          "& .MuiChip-root.c_unmodified": {
            color: "rgb(13, 60, 97)",
            backgroundColor: "rgb(232, 244, 253)",
          },
        }}
      />
    </Dialog>
  );
}
